// US-AUTH-02 / REQ-1.7 -- idle session guard.
//
// Call this primitive from any screen that requires an authenticated
// session. It:
//
//   1. Installs document-level activity listeners (mousemove, keydown,
//      click, touchstart) with a throttle so we only call
//      `session_touch` at most once per ACTIVITY_TOUCH_INTERVAL_MS.
//   2. Polls every POLL_INTERVAL_MS. If the session's absolute expiry
//      has lapsed with no recent activity, calls `onExpired()` -- the
//      caller is expected to route back to account selection.
//
// Everything tears down cleanly via Solid's `onCleanup` when the
// consuming component unmounts.

import { authLogout, sessionTouch } from '@chorepop/core/client';
import { createSignal, onCleanup, onMount } from 'solid-js';

const ACTIVITY_TOUCH_INTERVAL_MS = 60_000; // only refresh at most once a minute
const POLL_INTERVAL_MS = 30_000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart'] as const;

type Args = {
  sessionId: string;
  /** Absolute expiry on the current session. Used by the poll loop to
   *  detect an already-lapsed session without waiting for activity. */
  initialExpiresAtUtc: string;
  onExpired: () => void;
  onForegroundLost?: () => void;
};

export function useIdleSessionGuard(args: Args): () => string {
  const [expiresAt, setExpiresAt] = createSignal(args.initialExpiresAtUtc);
  let lastTouchMs = 0;
  let touching = false;
  let ending = false;

  const endSession = () => {
    if (ending) return;
    ending = true;

    // Route away immediately so a suspended webview can never reveal the
    // authenticated screen when it is resumed. Backend invalidation is
    // intentionally best-effort: the device may already be offline or the OS
    // may freeze the webview before the promise settles.
    void authLogout({ sessionId: args.sessionId });
    (args.onForegroundLost ?? args.onExpired)();
  };

  const touch = async () => {
    if (touching) return;
    touching = true;
    try {
      const res = await sessionTouch({ sessionId: args.sessionId });
      if (!res.ok) return; // transient backend error; poll loop will retry
      if (res.value.outcome === 'expired') {
        args.onExpired();
        return;
      }
      setExpiresAt(res.value.expiresAtUtc);
    } finally {
      touching = false;
    }
  };

  const onActivity = () => {
    const now = Date.now();
    if (now - lastTouchMs < ACTIVITY_TOUCH_INTERVAL_MS) return;
    lastTouchMs = now;
    void touch();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') endSession();
  };

  onMount(() => {
    for (const ev of ACTIVITY_EVENTS) {
      document.addEventListener(ev, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', endSession);
    const poll = window.setInterval(() => {
      const now = Date.now();
      const exp = new Date(expiresAt()).getTime();
      if (exp <= now) {
        args.onExpired();
      }
    }, POLL_INTERVAL_MS);

    onCleanup(() => {
      for (const ev of ACTIVITY_EVENTS) {
        document.removeEventListener(ev, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', endSession);
      window.clearInterval(poll);
    });
  });

  return expiresAt;
}
